import { useEffect, useState } from 'react';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import useTheme from '../../hooks/ui/useTheme';
import { ORDER_FULFILLMENT_STATUSES, ORDER_METHODS, PUSHER_EVENTS, SCREENS } from '../../services/exports/Constants';
import { Order } from '../../services/exports/Interfaces';
import LoadingIndicator from '../loading/LoadingIndicator';
import usePusher from '../../hooks/global/usePusher';
import useFocus from '../../hooks/utility/useFocus';
import { orderTrackingChannel } from '../../services/pusher/Channels';
import useToast from '../../hooks/utility/useToast';
import { useTranslation } from 'react-i18next';
import { FeedbackLabelTypes } from '../../services/exports/Types';
import useNavigate from '../../hooks/navigation/useNavigate';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';

export default function OngoingOrder() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OngoingOrder' });

  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const theme = useTheme();
  const { navigate } = useNavigate();
  const toast = useToast();
  const { formatTime } = useAppMethods();
  const { ongoingOrder, refreshOngoingOrder, removeOngoingOrder } = useOrderFlow();
  const { connect, disconnect } = usePusher();

  const [deletingOrder, setDeletingOrder] = useState(false);

  useFocus(
    () => initData(),
    () => disconnect(),
  );

  useEffect(() => {
    return () => disconnect();
  }, []);

  async function initData() {
    refreshOngoingOrder();
    connectPusher();
  }

  async function connectPusher() {
    if (!ongoingOrder?.id) {
      return;
    }

    const pusher = await connect();

    pusher.subscribe(orderTrackingChannel(ongoingOrder.uuid)).bind(PUSHER_EVENTS.ORDER_UPDATED, orderUpdatedHandler);
  }

  function getToastConfig(data: Partial<Order>): { message: string; type?: FeedbackLabelTypes } {
    if (ongoingOrder?.fulfillment.status === data.fulfillment.status) {
      return {
        message: t('order_status_toast.order_preparation_up_time_changed', {
          number: data.fulfillment.number,
          type: data.method === ORDER_METHODS.PICKUP ? t('order_type.pickup') : t('order_type.delivery'),
          time: formatTime(data.fulfillment.estimated_pickup_time),
        }),
      };
    }

    switch (data.fulfillment?.status) {
      case ORDER_FULFILLMENT_STATUSES.REVIEWED:
        return {
          message: t('order_status_toast.order_reviewed', {
            number: data.fulfillment.number,
          }),
        };
      case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        return {
          message: t('order_status_toast.order_confirmed', {
            number: data.fulfillment.number,
            time: formatTime(data.fulfillment.estimated_pickup_time),
          }),
          type: 'Success',
        };
      case ORDER_FULFILLMENT_STATUSES.PREPARING:
        return {
          message: t('order_status_toast.order_preparing', {
            number: data.fulfillment.number,
          }),
        };
      case ORDER_FULFILLMENT_STATUSES.REJECTED:
        return {
          message: t('order_status_toast.order_rejected', {
            number: data.fulfillment.number,
          }),
          type: 'Error',
        };
      default:
        return {
          message: t('order_status_toast.order_preparation_up_time_changed', {
            number: data.fulfillment.number,
            type: data.method === ORDER_METHODS.PICKUP ? t('order_type.pickup') : t('order_type.delivery'),
            time: formatTime(data.fulfillment.estimated_pickup_time),
          }),
        };
    }
  }

  function orderUpdatedHandler(data: Partial<Order>) {
    if (
      data.fulfillment.status !== ongoingOrder.fulfillment.status ||
      data.fulfillment.estimated_pickup_time !== ongoingOrder.fulfillment.estimated_pickup_time
    ) {
      const config = getToastConfig(data);

      switch (config?.type) {
        case 'Success':
          toast.success(config?.message);
          break;
        case 'Error':
          toast.error(config?.message);
          break;
        default:
          toast(config?.message);
          break;
      }
    }

    refreshOngoingOrder();
  }

  async function deleteOrder() {
    setDeletingOrder(true);
    removeOngoingOrder(ongoingOrder.id);
    disconnect();

    setTimeout(() => setDeletingOrder(true), 500);
  }

  function goToOrder() {
    navigate(`${SCREENS.ORDER_DETAILS}/${ongoingOrder.uuid}`);
  }

  const getColor = () => {
    if (!company?.has_scheduled_orders) {
      return theme.color.warning;
    }

    switch (ongoingOrder.fulfillment.status) {
      case ORDER_FULFILLMENT_STATUSES.REJECTED:
        return theme.color.danger;
      case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        return theme.color.primary;
      default:
        return theme.color.orange.DEFAULT;
    }
  };

  function renderLabel() {
    const getTitle = () => {
      if (!company?.has_scheduled_orders) {
        return t('ongoing_order');
      }

      switch (ongoingOrder.fulfillment.status) {
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return t('status.rejected.title');
        default:
          return t('ongoing_order');
      }
    };

    const getDescription = () => {
      if (!company?.has_scheduled_orders) {
        return t('view_more');
      }

      switch (ongoingOrder.fulfillment.status) {
        case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        case ORDER_FULFILLMENT_STATUSES.REJECTED:
          return t('status.rejected.description');
        case ORDER_FULFILLMENT_STATUSES.CREATED:
          return t('status.created.description');
        case ORDER_FULFILLMENT_STATUSES.REVIEWED:
          return t('status.reviewed.description');
        default:
          return null;
      }
    };

    return (
      <div className={labelContainer}>
        <h6 className="text-white">
          {getTitle()} <p className="inline font-bold">#{ongoingOrder.fulfillment.number}</p>
        </h6>
        <p className="text-sm text-white">{getDescription()}</p>
      </div>
    );
  }

  function renderCloseIcon() {
    return !deletingOrder ? (
      <button className={closeIconContainer} onClick={deleteOrder}>
        <CloseIcon color={theme.color.white['100']} />
      </button>
    ) : (
      <LoadingIndicator customColor={theme.color.white['100']} sm={18} md={18} lg={18} />
    );
  }

  function renderPickUpDeliveryTime() {
    return (
      <div className={timeContainer}>
        <h6 className="text-white">
          {ongoingOrder.method === ORDER_METHODS.PICKUP ? t('pickup') : t('delivery')}{' '}
          <p className="inline font-medium">{formatTime(ongoingOrder.fulfillment.estimated_pickup_time)}</p>
        </h6>
      </div>
    );
  }

  return (
    <div className={container}>
      <div className={backgroundContainer} style={{ backgroundColor: getColor() }} onClick={goToOrder} />
      <div className="cursor-pointer" onClick={goToOrder}>
        {renderLabel()}
      </div>

      {ongoingOrder.fulfillment.status === ORDER_FULFILLMENT_STATUSES.REJECTED
        ? renderCloseIcon()
        : renderPickUpDeliveryTime()}
    </div>
  );
}

const container =
  'relative flex flex-row w-full justify-between items-center lg:px-lg px-md cursor-pointer h-[58px] rounded-xl overflow-hidden';

const backgroundContainer = 'absolute inset-0';

const labelContainer = 'relative';

const timeContainer = 'flex flex-row relative';

const closeIconContainer = 'flex pt-nano pl-xs pb-xs relative';
